import { useCallback, useContext, useMemo, useState } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { StaticPropertyMap } from 'components/Common/PropertyMap'
import { formatMarkers, overlapMarkers } from 'components/Common/PropertyMap/utils'
import SearchMapCard from 'components/Search/SearchResultsMap/SearchMapCard/SearchMapCard'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import ModalContext from 'contexts/ModalContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { isBedbankOffer, isBundleOffer, isFlashHotelOffer, isLEHotel } from 'lib/offer/offerTypes'
import { MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import offerPageURL from 'lib/offer/offerPageURL'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { NEARBY_MAP_MARKER_TYPE, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { hasMapCoordinates } from './mapCoordinates'

interface Props {
  offer: App.HotelOffer | App.BundleOffer | App.BedbankOffer
  height?: number
}

type OfferMapCardOffer =
  | App.HotelOffer
  | App.HotelOfferSummary
  | App.BedbankOffer
  | App.BedbankOfferSummary

interface OfferMapMarker extends App.MapMarker {
  offer: OfferMapCardOffer
  openGoogleMapsOnSelect: false
}

const NEARBY_OFFERS_LIMIT = 10
const NEARBY_OFFER_TYPES: Array<App.OfferListOfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
]
const StyledClickableLink = styled(ClickableLink)`
  pointer-events: auto;
  width: 100%;
`

function getMarkerAddress(address?: string | App.BedbankPropertyAddress) {
  if (!address || typeof address === 'string') {
    return address
  }

  if (address.obfuscationRequired) {
    return [
      address.city,
      address.stateProvinceCode,
      address.stateProvinceName,
      address.postalCode,
      address.countryName,
    ].filter(Boolean).join(' ')
  }

  return [
    address.line1,
    address.line2,
    address.city,
    address.stateProvinceCode,
    address.stateProvinceName,
    address.postalCode,
    address.countryName,
  ].filter(Boolean).join(' ')
}

function isSearchMapCardOffer(offer: App.AnyOffer | undefined): offer is OfferMapCardOffer {
  return isBedbankOffer(offer) || isLEHotel(offer)
}

function getNearbyPropertyId(offer: Props['offer'], propertyId: string): string {
  const propertyIdPrefix = isBedbankOffer(offer) ? 'bedbank' : 'le'
  return `${propertyIdPrefix}:${propertyId}`
}

function getBundlePropertyMarkers(bundledOffers: App.BundleOffer['bundledOffers']): Array<App.MapMarker> {
  return Object.values(bundledOffers ?? {}).flatMap((bundleOffer) => {
    if (!hasMapCoordinates(bundleOffer.property)) {
      return []
    }

    return [{
      id: bundleOffer.property.id,
      type: PROPERTY_MAP_MARKER_TYPE,
      latitude: bundleOffer.property.latitude,
      longitude: bundleOffer.property.longitude,
      name: bundleOffer.property.name,
      address: bundleOffer.property.address,
    }]
  })
}

function getMainPropertyMarker(offer: Props['offer'], property?: App.OfferProperty | App.BedbankProperty): OfferMapMarker | undefined {
  if (!property || !hasMapCoordinates(property)) {
    return undefined
  }

  if (!isSearchMapCardOffer(offer)) {
    return undefined
  }

  return {
    id: property.id,
    type: PROPERTY_MAP_MARKER_TYPE,
    latitude: property.latitude,
    longitude: property.longitude,
    name: property.name,
    address: getMarkerAddress(property.address),
    openGoogleMapsOnSelect: false,
    offer,
  }
}

function getNearbyOfferMarker(nearbyOffer: OfferMapCardOffer, currentPropertyId?: string): OfferMapMarker | undefined {
  if (!nearbyOffer.property || nearbyOffer.property.id === currentPropertyId || !hasMapCoordinates(nearbyOffer.property)) {
    return undefined
  }

  return {
    id: nearbyOffer.id,
    type: NEARBY_MAP_MARKER_TYPE,
    latitude: nearbyOffer.property.latitude,
    longitude: nearbyOffer.property.longitude,
    imageId: nearbyOffer.image.id,
    name: nearbyOffer.name,
    subName: nearbyOffer.property.name,
    locationHeading: isLEHotel(nearbyOffer) ? nearbyOffer.locationHeading : undefined,
    locationSubheading: isLEHotel(nearbyOffer) ? nearbyOffer.locationSubheading : undefined,
    address: getMarkerAddress(nearbyOffer.property.address),
    openGoogleMapsOnSelect: false,
    offer: nearbyOffer,
  }
}

function getOfferFromMarker(marker: App.MapMarker): OfferMapCardOffer | undefined {
  if (!isSearchMapCardOffer(marker.offer)) {
    return undefined
  }

  return marker.offer
}

function OfferLocationMap({ offer, height = 258 }: Props) {
  const { property, id: offerId } = offer
  const bundledOffers = isBundleOffer(offer) ? offer.bundledOffers : undefined
  const [hasMapBeenInView, setHasMapBeenInView] = useState(false)
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')
  const intl = useIntl()

  const nearbyFilters = useMemo<App.OfferListFilters | undefined>(() => {
    if (!property?.id || bundledOffers) {
      return undefined
    }

    return {
      propertyId: getNearbyPropertyId(offer, property.id),
      include: ['nearby'],
      limit: NEARBY_OFFERS_LIMIT,
      isStream: true,
      offerTypes: NEARBY_OFFER_TYPES,
    }
  }, [bundledOffers, offer, property?.id])

  const nearbyOfferList = useOfferList(nearbyFilters, {
    disabled: !nearbyFilters || !hasMapBeenInView,
    excludedIds: offerId ? [offerId] : undefined,
  })
  const [loadedNearbyOffers] = useOffers<App.AnyOffer>(nearbyOfferList.offerIds)

  const nearbyOffers = useMemo(() => {
    return loadedNearbyOffers.filter((nearbyOffer): nearbyOffer is OfferMapCardOffer => {
      return isSearchMapCardOffer(nearbyOffer) && hasMapCoordinates(nearbyOffer.property)
    })
  }, [loadedNearbyOffers])

  const markers = useMemo<Array<App.MapMarker>>(() => {
    if (bundledOffers) {
      return getBundlePropertyMarkers(bundledOffers)
    }

    const mainPropertyMarker = getMainPropertyMarker(offer, property)
    const nearbyOfferMarkers = nearbyOffers.map(nearbyOffer => getNearbyOfferMarker(nearbyOffer, property?.id)).filter((marker): marker is OfferMapMarker => !!marker)

    return overlapMarkers(formatMarkers([
      ...(mainPropertyMarker ? [mainPropertyMarker] : []),
      ...nearbyOfferMarkers,
    ]))
  }, [bundledOffers, nearbyOffers, offer, property])

  const renderNearbyOfferCard = useCallback((
    nearbyOffer: OfferMapCardOffer,
    onCloseClick: () => void,
    onNavigateClick?: () => void,
  ) => {
    return (
      <StyledClickableLink
        to={offerPageURL(nearbyOffer)}
        preventSwipeClick
        onClick={onNavigateClick}
      >
        <SearchMapCard
          offer={nearbyOffer}
          onClose={onCloseClick}
        />
      </StyledClickableLink>
    )
  }, [])

  const renderInfoWindow = useCallback((marker: App.MapMarker, onCloseClick: () => void, onNavigateClick?: () => void) => {
    const markerOffer = getOfferFromMarker(marker)
    if (!markerOffer) {
      return null
    }

    return renderNearbyOfferCard(markerOffer, onCloseClick, onNavigateClick)
  }, [renderNearbyOfferCard])

  const handleSelectMarker = useCallback((marker: App.MapMarker) => {
    if (!isMobile) {
      return
    }

    const markerOffer = getOfferFromMarker(marker)
    if (!markerOffer) {
      return
    }

    showModal((resolve) => (
      <ModalBase mode="default" height="auto" onClose={() => resolve()}>
        {renderNearbyOfferCard(markerOffer, () => resolve())}
      </ModalBase>
    ))
  }, [isMobile, renderNearbyOfferCard, showModal])

  const handleMapScrolledIntoView = useCallback(() => {
    setHasMapBeenInView(true)
  }, [])

  const mapboxAccessTokenUseCase = isFlashHotelOffer(offer) ?
    MAPBOX_ACCESS_TOKEN_USE_CASES.flashHotel :
    MAPBOX_ACCESS_TOKEN_USE_CASES.property
  const fallbackOfferTitle = intl.formatMessage({
    id: 'npp1CgNTOcl7BdLssvTL',
    defaultMessage: 'Interactive offer map',
    description: 'Default title for map marker info window when offer or property name is not available',
  })

  return (
    <StaticPropertyMap
      height={height}
      markers={markers}
      focusOnFirstMarker={!bundledOffers}
      disablePanOnMarkerSelect
      enableNearbyMarkerFullscreen={!isMobile}
      highlightedMarkerId={bundledOffers ? undefined : property?.id}
      mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
      modalTitle={property?.name ?? fallbackOfferTitle}
      onScrolledIntoView={handleMapScrolledIntoView}
      onSelectMarker={bundledOffers ? undefined : handleSelectMarker}
      renderInfoWindow={bundledOffers || isMobile ? undefined : renderInfoWindow}
    />
  )
}

export default OfferLocationMap
